import { AdminRole } from "@workspace/admin-domain/src/features/role/schema";
import type {
  TPermission,
  TResource,
} from "@workspace/domain/src/utils/permissions";
import { t } from "i18next";
import * as z from "zod";

// CONSTANTS

export const ROLE_SORT_OPTIONS: {
  label: string;
  kv: `${AdminRole["SortBy"]}_${AdminRole["SortOrder"]}`;
  value: {
    key: AdminRole["SortBy"];
    order: AdminRole["SortOrder"];
  };
}[] = [
  {
    label: t("roles.sort.name_asc"),
    kv: "name_asc",
    value: {
      key: "name",
      order: "asc",
    },
  },
  {
    label: t("roles.sort.name_desc"),
    kv: "name_desc",
    value: {
      key: "name",
      order: "desc",
    },
  },
  {
    label: t("roles.sort.numberOfPermissions_asc"),
    kv: "numberOfPermissions_asc",
    value: {
      key: "numberOfPermissions",
      order: "asc",
    },
  },
  {
    label: t("roles.sort.numberOfPermissions_desc"),
    kv: "numberOfPermissions_desc",
    value: {
      key: "numberOfPermissions",
      order: "desc",
    },
  },
  {
    label: t("roles.sort.numberOfEmployees_asc"),
    kv: "numberOfEmployees_asc",
    value: {
      key: "numberOfEmployees",
      order: "asc",
    },
  },
  {
    label: t("roles.sort.numberOfEmployees_desc"),
    kv: "numberOfEmployees_desc",
    value: {
      key: "numberOfEmployees",
      order: "desc",
    },
  },
] as const;

// Modify permissions = create, update, delete
const MODIFY_ACTIONS = ["create", "update", "delete"] as const;

export function hasViewPermission(
  permissions: string[],
  resource: string,
): boolean {
  return permissions.includes(`${resource}:get`);
}

export function hasFullModifyPermission(
  permissions: string[],
  resource: string,
): boolean {
  return MODIFY_ACTIONS.every((action) =>
    permissions.includes(`${resource}:${action}`),
  );
}

export function getViewPermission(resource: TResource): TPermission {
  return `${resource}:get`;
}

export function getModifyPermissions(resource: TResource): TPermission[] {
  return MODIFY_ACTIONS.map((action) => `${resource}:${action}` as TPermission);
}

// SCHEMAS

export const RoleFiltersSchema = z.object({
  sortBy: z.enum(AdminRole.SortBy).optional(),
  sortOrder: z.enum(AdminRole.SortOrder).optional(),
  search: z.string().optional(),
  permissions: z.string().array().optional(),
});
export type RoleFiltersSchema = z.infer<typeof RoleFiltersSchema>;
